/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ServiceInstance } from "app/models/service-instance";
import { DenverMattressReaderStateEnum, DenverMattressService, ValidationAttemptLineItem, ValidationInfo, ValidationState } from "app/services/denver-mattress.service";

@Component({
  templateUrl: './dm-portal-validation.dashboard.component.html',
  styleUrls: ['./dm-portal-validation.dashboard.component.css']
})
export class DenverMattressPortalValidationDashboardComponent implements OnInit, OnDestroy {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
@ViewChild('successErrorDialog') 
simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  portal: ServiceInstance;
  validationInfo: ValidationInfo;
  readerState: String;

  isClearOrderBeingProcessed = false;
  isReaderActionBeingProcessed = false;
  isProcessActionBeingProcessed = false;

  isDestroyed = false;

  /** Constructor */
  constructor(
    private denverMattressService: DenverMattressService,
    private route: ActivatedRoute,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  /**
   * Initializes the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnInit(): void {
    console.log(`ngOnInit()`);
    console.log("state Info :- " + this.validationInfo);
    this.route.data.subscribe(
      (data: {
        portal: ServiceInstance,
        validationInfo: ValidationInfo,
        readerState: String
      }) => {
        console.log(`Obtained Portal: '${data?.portal?.id}/${data?.portal?.name}'`);
        this.portal = data?.portal;
        console.log(this.portal.name);

        console.log(`Obtained State: '${JSON.stringify(data?.validationInfo)}'`);
        this.validationInfo = data?.validationInfo;

        console.log(`Obtained Reader State: '${JSON.stringify(data?.readerState)}'`);
        this.readerState = data?.readerState;
        console.log(this.readerState);

        if (this.validationInfo.state == ValidationState.ValidatingOrder) {
          console.log(`Validate Info :-  '${JSON.stringify(this.validationInfo)}'`);
        }
        else if (this.validationInfo.state == ValidationState.WaitingOnOrderInfo) {
          console.log('No Order to display');
        }
      });
    setTimeout(this.refreshData.bind(this), 1000);
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  displayTableAndFunctions(): boolean {
    return (this.validationInfo.state == ValidationState.ValidatingOrder || this.validationInfo.state == ValidationState.ProcessingOrder);
  }

  getDisplayState(displayState: ValidationState): string {
    switch(displayState){
      case ValidationState.LoadingOrderInfo :
        return "Loading Order Information";
      case ValidationState.ProcessingOrder :
        return "Processing Order";
      case ValidationState.ValidatingOrder :
        return "Validating Order";
      case ValidationState.WaitingOnOrderInfo :
        return "Waiting on Order Information";
      default:
        return "Unknown";
    }
  }
  

  private async refreshData(): Promise<void> {
    if (!this.isDestroyed) {
      const state = await this.denverMattressService.getValidationInfo(this.portal.id).toPromise();
      if (JSON.stringify(this.validationInfo) != JSON.stringify(state)) {
        this.validationInfo = state;
        this.changeDetectorRef.detectChanges();
      }

      if (!this.isDestroyed) {
        setTimeout(this.refreshData.bind(this), 1000);
      }
    }
  }

  public async getReaderStatus(): Promise<String> {
    let readerStatus: String = null;
    try {
      readerStatus = await this.denverMattressService.getReaderStatus(this.portal.id).toPromise();
    } catch (error) {
      this.handleError(error);
      return;
    }
    this.readerState = readerStatus;
  }

  isReaderStarted(): boolean {
    return this.readerState == DenverMattressReaderStateEnum.Started;
  }

  onStartReaderClicked(): void {
    console.log(`onStartReaderClicked()`);
    if (!this.isReaderActionBeingProcessed) {
      this.isReaderActionBeingProcessed = true;
      this.indefiniteProgressDialog.show("Processing", "Starting Reader...");
      this.startReader();
    }
  }

  onStopReaderClicked(): void {
    console.log(`onStopReaderClicked()`);
    if (!this.isReaderActionBeingProcessed) {
      this.isReaderActionBeingProcessed = true;
      this.indefiniteProgressDialog.show("Processing", "Stopping Reader...");
      this.stopReader();
    }
  }

  private async startReader(): Promise<void> {
    console.log(`startReader()`);

    try {
      await this.denverMattressService.startReader(this.portal.id).toPromise();
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.isReaderActionBeingProcessed = false;
      this.handleError(error);
      return;
    }

    this.indefiniteProgressDialog.hide();
    this.readerState = DenverMattressReaderStateEnum.Started;
    this.isReaderActionBeingProcessed = false;
  }

  private async stopReader(): Promise<void> {
    console.log(`stopReader()`);

    try {
      await this.denverMattressService.stopReader(this.portal.id).toPromise();
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.isReaderActionBeingProcessed = false;
      this.handleError(error);
      return;
    }

    this.indefiniteProgressDialog.hide();
    this.readerState = DenverMattressReaderStateEnum.Stopped;
    this.isReaderActionBeingProcessed = false;
  }


  /**
   * Called by UI whent he "Process" button is clicked.
   * Starts the "Process" process if it isn't already being done.
   */
  onProcessClicked(): void {
    if (!this.isProcessActionBeingProcessed) {
      this.isProcessActionBeingProcessed = true;
      this.indefiniteProgressDialog.show("Processing", "Updating Order Information...");
      this.processOrder();
    }
  }

  /**
   * Processes the Order information from the UI by calling the API with the Line Items 
   * selected to send and any override quantities that were specified
   */
  private async processOrder(): Promise<void> {
    try {
      await this.denverMattressService.processValidation(this.portal.id).toPromise();
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.handleError(error);
      return;
    } finally {
      this.indefiniteProgressDialog.hide();
      this.isProcessActionBeingProcessed = false;
      return;
    }
  }

  // Navigate to select Page
  onBackButtonClicked() {
    try {
      this.router.navigate(['denver-mattress', 'portal-validation', 'select-portal'], {});
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * being in error. (RED)
   */
  isErrorRow(lineItem: ValidationAttemptLineItem): boolean {
    return lineItem?.quantity_read > lineItem?.quantity_required && lineItem?.quantity_required > 0;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighted as
   * being unknown. (GRAY)
   */
  isUnknownRow(lineItem: ValidationAttemptLineItem): boolean {
    return lineItem?.quantity_required == 0;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * being completed successfully. (GREEN)
   */
  isSuccessRow(lineItem: ValidationAttemptLineItem): boolean {
    return lineItem?.quantity_read == lineItem?.quantity_required;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * partially fulfilled. (YELLOW)
   */
  isPartialRow(lineItem: ValidationAttemptLineItem): boolean {
    return (lineItem?.quantity_read >= 0 && lineItem?.quantity_required > 0 && lineItem?.quantity_required > lineItem?.quantity_read);
  }

  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }

    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}
